import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, Subject, of } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';

import { addDays, fromUnixTime, isBefore } from 'date-fns';
import { jwtDecode } from 'jwt-decode';

import { DRAFT_TRAINING_ANAMNESIS_STORAGE, REFRESH_TOKEN_NAME, TOKEN_NAME } from '@app/constants/constants';
import { ENVIRONMENT } from '@app/constants/environment.token';
import { AuthPWAResponse, AuthResponse } from '@app/models/auth-response.model';
import { User } from '@app/models/user.model';
import { CookieService } from '@app/services/cookie.service';

/** Authentication service. Manages JWT tokens, user state and login/logout flows. */
@Injectable({ providedIn: 'root' })
export class AuthService {
    private readonly http = inject(HttpClient);
    private readonly router = inject(Router);
    private readonly cookieService = inject(CookieService);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly env = inject(ENVIRONMENT);

    /** Current logged-in user profile. Null = unauthenticated. */
    readonly currentUser = signal<User | null>(null);

    private retrieveUserInProgress = false;
    private readonly retrieveUserSource = new Subject<User>();
    readonly retrieveUser$ = this.retrieveUserSource.asObservable();

    /* ------------------------------ HTTP REQUEST ------------------------------ */

    /**
     * Returns the logged user profile from cache or from GET /me.
     * @param forceUpdate When true, ignores cache and fetches from API.
     */
    getUser(forceUpdate = false): Observable<User> {
        const cached = this.currentUser();
        if (cached && !forceUpdate) {
            return of(cached);
        }
        return this.handleMultipleApiRequest();
    }

    retrieveUser(): Observable<User> {
        return this.http.get<User>('me').pipe(tap((profile) => this.currentUser.set(profile)));
    }

    login(email: string, password: string, rememberMe: boolean): Observable<AuthResponse> {
        return this.http
            .post<AuthResponse>('login', { email, password, remember_me: rememberMe })
            .pipe(tap((res) => this.setTokenAndRefreshToken(res.token, res.refresh_token)));
    }

    loginPWA(email: string, password: string, rememberMe = true): Observable<AuthPWAResponse> {
        return this.http.post<AuthPWAResponse>('pwa/login', { email, password, remember_me: rememberMe }).pipe(
            tap((res) => {
                this.setTokenAndRefreshToken(res.token, res.refresh_token);
                this.setMstWpCookie(res.cookie_name, res.cookie);
            }),
        );
    }

    loginCookie(cookie: string): Observable<AuthResponse> {
        this.removeTokenAndRefreshToken();
        return this.http
            .post<AuthResponse>('login', { cookie })
            .pipe(tap((res) => this.setTokenAndRefreshToken(res.token, res.refresh_token)));
    }

    logout(): Observable<unknown> {
        localStorage.removeItem(DRAFT_TRAINING_ANAMNESIS_STORAGE);
        return this.http.post<unknown>('logout', {}).pipe(
            tap({
                next: () => this.goToLogin(),
                error: () => this.goToLogin(),
            }),
        );
    }

    refreshToken(): Observable<AuthResponse> {
        const refreshToken = this.cookieService.get(REFRESH_TOKEN_NAME);
        return this.http
            .post<AuthResponse>('token/refresh', { refresh_token: refreshToken })
            .pipe(tap((res) => this.setTokenAndRefreshToken(res.token, res.refresh_token)));
    }

    validateResetPasswordToken(token: string | null): Observable<unknown> {
        return this.http.post<unknown>('validate_user_token', { encrypted_user_token: token });
    }

    /* ---------------------------------- STATE --------------------------------- */

    isLoggedIn(): boolean {
        return !!this.cookieService.get(TOKEN_NAME);
    }

    /* ------------------------------ TOKEN HELPERS ----------------------------- */

    getTokenExpirationDate(token: string): Date | null {
        try {
            const decoded = jwtDecode<{ exp?: number }>(token);
            if (decoded.exp === undefined) return null;
            return fromUnixTime(decoded.exp);
        } catch {
            return null;
        }
    }

    isTokenExpired(token?: string): boolean {
        const authToken = token ?? this.cookieService.get(TOKEN_NAME);
        if (!authToken) return true;
        const date = this.getTokenExpirationDate(authToken);
        if (!date) return false;
        return isBefore(date, new Date());
    }

    setTokenAndRefreshToken(token: string, refreshToken: string): void {
        const expires = addDays(new Date(), 30);
        this.cookieService.set(TOKEN_NAME, token, expires);
        this.cookieService.set(REFRESH_TOKEN_NAME, refreshToken, expires);
    }

    removeTokenAndRefreshToken(): void {
        this.cookieService.remove(TOKEN_NAME);
        this.cookieService.remove(REFRESH_TOKEN_NAME);
    }

    goToLogin(): void {
        this.removeTokenAndRefreshToken();
        this.removeMstWpCookie();
        this.currentUser.set(null);
        const isStandalone = isPlatformBrowser(this.platformId) && window.matchMedia('(display-mode: standalone)').matches;
        if (isStandalone || this.env.localMode) {
            this.router.navigate(['/login']);
        } else {
            setTimeout(() => {
                window.location.href = `${this.env.wpUrl}my-account/`;
            }, 500);
        }
    }

    /* ---------------------------- PRIVATE - METHODS --------------------------- */

    private handleMultipleApiRequest(): Observable<User> {
        if (this.retrieveUserInProgress) {
            return new Observable<User>((observer) => {
                this.retrieveUser$.subscribe((profile) => {
                    observer.next(profile);
                    observer.complete();
                });
            });
        }
        this.retrieveUserInProgress = true;
        return this.retrieveUser().pipe(
            tap((profile) => this.retrieveUserSource.next(profile)),
            finalize(() => (this.retrieveUserInProgress = false)),
        );
    }

    /* ---------------------------- COOKIE - METHODS ---------------------------- */

    private setMstWpCookie(cookieName: string, cookie: string): void {
        const expires = addDays(new Date(), 30);
        this.cookieService.set(cookieName, cookie, expires);
    }

    private removeMstWpCookie(): void {
        const all = this.cookieService.getAll();
        const wpKey = Object.keys(all).find((key) => key.includes('wordpress_logged_in'));
        if (wpKey) this.cookieService.remove(wpKey);
    }
}
